import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import { cn } from "@/lib/utils";

function Checkbox({ className, ...props }: CheckboxPrimitive.Root.Props) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        // Base — 16×16, fully round (radius-sm = 10px in our tokens,
        // which caps to a circle on a 16px square), 1.5px input-border
        // on white per Figma LUARY-DS2. Disabled paints with muted
        // tokens (bg + border + check foreground), not opacity. Checked
        // fills with primary. Disabled-checked overrides back to muted
        // but keeps the 1.5px border (per Figma).
        //
        // NB: Base UI Checkbox renders a <span>, so `:disabled` and
        // `aria-invalid` pseudo / attribute selectors don't match the
        // visible element. Use Base UI's `data-disabled` /
        // `data-invalid` data attributes instead.
        "peer relative flex size-4 shrink-0 items-center justify-center rounded-sm border-[1.5px] border-input bg-background transition-colors outline-none",
        "after:absolute after:-inset-x-3 after:-inset-y-2",
        "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
        // No `group-has-disabled/field:opacity-50` — when the Field is
        // disabled, its `disabled` prop propagates to the Checkbox,
        // which then renders the muted `data-disabled:` treatment.
        // Stacking the field-level opacity-50 on top would double-dim
        // the in-field control vs the standalone one. FieldTitle /
        // FieldDescription still handle their own text dim via
        // `group-data-[disabled=true]/field:opacity-50`.
        "data-disabled:cursor-not-allowed data-disabled:border-border data-disabled:bg-muted data-disabled:text-muted-foreground",
        // Invalid styling on both selectors: `data-invalid` is what
        // Base UI sets when the checkbox is wrapped in a Field that's
        // invalid; `aria-invalid` is what consumers commonly set
        // manually for ad-hoc error states outside of Field.
        "data-invalid:border-destructive data-invalid:ring-3 data-invalid:ring-destructive/20 dark:data-invalid:border-destructive/50 dark:data-invalid:ring-destructive/40",
        "aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40",
        "data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground",
        // `!` so the disabled-checked override unambiguously beats the
        // plain `data-checked:` rules above regardless of Tailwind's
        // variant ordering in the compiled stylesheet.
        "data-checked:data-disabled:border-border! data-checked:data-disabled:bg-muted! data-checked:data-disabled:text-muted-foreground!",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current transition-none"
      >
        {/* Custom SVG instead of Phosphor's Check — Figma specifies a
            specific vector path with strokeWidth 1.75 and round caps,
            which isn't a stock Phosphor weight. */}
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-3.5"
          aria-hidden="true"
        >
          <title>Checked</title>
          <path d="M4 8l3 3 6-6" />
        </svg>
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
